/**
 * shadcn/Tailwind replacement for GenericDeleteConfirmDialog.
 * Confirmation dialog for deleting entities.
 */

import React from 'react';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { DialogErrorAlert } from './DialogErrorAlert';
import { cn } from '../../lib/utils';

interface ShadcnDeleteConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void> | void;
    entityType: string;
    entityName: string;
    isDeleting?: boolean;
    error?: string | null;
    additionalWarning?: string;
    title?: string;
    confirmMessage?: string;
}

export const ShadcnDeleteConfirmDialog: React.FC<ShadcnDeleteConfirmDialogProps> = ({
    open,
    onClose,
    onConfirm,
    entityType,
    entityName,
    isDeleting = false,
    error = null,
    additionalWarning,
    title,
    confirmMessage,
}) => {
    if (!open) return null;

    const handleConfirm = async () => {
        await onConfirm();
    };

    const displayTitle = title || `Delete ${entityType}`;
    const displayMessage = confirmMessage ||
        `Are you sure you want to delete the ${entityType.toLowerCase()} "${entityName}"?`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-background/80 backdrop-blur-sm"
                onClick={isDeleting ? undefined : onClose}
            />

            {/* Dialog */}
            <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-lg mx-4">
                {/* Header */}
                <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
                    <AlertTriangle size={20} className="text-destructive shrink-0" />
                    <h2 className="text-base font-semibold text-foreground">{displayTitle}</h2>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                    <p className="text-sm text-muted-foreground">{displayMessage}</p>

                    {/* Warning */}
                    <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/50 p-3">
                        <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                        <span className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                            This action cannot be undone.
                        </span>
                    </div>

                    {additionalWarning && (
                        <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50 p-3">
                            <span className="text-sm text-blue-800 dark:text-blue-200">{additionalWarning}</span>
                        </div>
                    )}

                    <DialogErrorAlert error={error} />

                    {entityName && (
                        <div className="rounded-md border border-border bg-muted/50 p-3">
                            <span className="block text-xs text-muted-foreground mb-0.5">{entityType}:</span>
                            <span className="text-sm font-medium text-foreground break-words">{entityName}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className={cn(
                            'h-9 px-4 rounded-md text-sm font-medium transition-colors',
                            'border border-input bg-background hover:bg-muted text-foreground',
                            isDeleting && 'opacity-50 pointer-events-none',
                        )}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isDeleting}
                        className={cn(
                            'inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-sm font-medium transition-colors',
                            'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                            isDeleting && 'opacity-70 pointer-events-none',
                        )}
                    >
                        {isDeleting ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Trash2 size={14} />
                        )}
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};
